<template>
    <div
        class="performance-table"
        :class="{ 'sticky-warning': !isFdaFriendly && isTooEarlyForScores }"
    >
        <wells-sub-header />
        <inference-availability-message v-if="!isFdaFriendly && isTooEarlyForScores" />

        <div class="performance-table-content">
            <b-table
                variant="light"
                :items="wells"
                :fields="fields"
                class="scrollable-part"
                sort-by="kidRank"
                select-mode="single"
                :tbody-tr-class="rowClass"
                :responsive="true"
                sticky-header
                hover
            >
                <template v-slot:cell(select)="row">
                    <div class="pr-2">
                        <compare-well-checkbox :well="row.item" />
                    </div>
                </template>

                <template v-slot:cell(status)="row">
                    <div class="decision-cell">
                        <div class="latest-pic" @click="selectWell(row.item)">
                            <img :src="row.value" :draggable="false" alt="" />
                            <well-status-overlay :well="row.item" />
                        </div>

                        <performance-table-well-status-dropdown :row="row" />
                    </div>
                </template>

                <template v-slot:cell(kidRank)="row">
                    <div
                        v-b-tooltip.hover
                        class="rank-cell"
                        :class="{ 'top-rank': row.item.kidRank < 4 }"
                        :title="$translate('ai_rank')"
                    >
                        <img src="/static/svg/rankcrown.svg" class="crown" alt="" />

                        <span class="rank-value">
                            <span v-if="getRankValueText(row.item)">
                                {{ getRankValueText(row.item) }}
                            </span>

                            <span class="score-hazard-icons-container">
                                <img
                                    v-if="degeneratedOocyteHazard(row.item)"
                                    v-b-tooltip.hover
                                    src="/static/svg/Hazard_Deg_white.svg"
                                    alt=""
                                    :title="$translate('degenerated_oocyte')"
                                />
                                <img
                                    v-else-if="pnHazard(row.item)"
                                    v-b-tooltip.hover
                                    src="/static/svg/Hazard_PN_white.svg"
                                    alt=""
                                    :title="$uiUtils.formatPnHazardMessage(pnCount(row.item))"
                                />
                                <img
                                    v-else-if="ducsHazard(row.item)"
                                    id="ducs_hazard_icon"
                                    v-b-tooltip.hover
                                    src="/static/svg/Hazard_Duc_white.svg"
                                    alt=""
                                    :title="$translate('duc_tooltip')"
                                />
                            </span>
                        </span>
                    </div>
                </template>

                <template v-slot:cell(inferenceData.kidScore)="row">
                    <kid-score
                        option="table-cell-option"
                        :well="row.item"
                        :color-background="false"
                    />
                </template>

                <template v-slot:cell(inferenceData.blastScore)="row">
                    <blast-score
                        option="table-cell-option"
                        :well="row.item"
                        :color-background="false"
                    />
                </template>

                <template v-slot:cell(inferenceData.pnCount)="row">
                    <span class="pn-count-cell" :class="{ hazard: pnHazard(row.item) }">
                        {{ row.value }}
                    </span>
                </template>
                <template v-for="col in istanbulEventColumns" v-slot:[`head(${col.key})`]="row">
                    <span :key="row.label" v-b-tooltip.hover :title="row.field.hoverText">{{
                        row.label
                    }}</span>
                </template>

                <template v-slot:cell(annotationHazard)="row">
                    <div class="abnormalities-cell">
                        <span
                            v-for="hazard in getHazards(row.item)"
                            :key="hazard"
                            :class="hazard.toLowerCase()"
                        >
                            {{ hazard }}
                        </span>
                    </div>
                </template>
                <template v-slot:cell(pgta)="row">
                    <div class="pgta-cell">
                        <well-pgta-dropdown :well="row.item" />
                    </div>
                </template>

                <template v-slot:cell(clinicGardner)="row">
                    <div class="gardner-cell">
                        <clinic-gardner :well="row.item" />
                    </div>
                </template>
            </b-table>
        </div>
    </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import {
    ALL_ISTANBUL_EVENTS,
    ALL_MK_EVENTS,
    INSTRUMENT_TYPE,
    TREATMENT_PAGE_VIEWS,
    TREATMENT_STATUS
} from '../../business/const'
import UIUtils from '../../business/utils/UIUtils'
import Utils from '../../business/utils/Utils'
import KidScore from './score-metrics/KidScore'
import BlastScore from './score-metrics/BlastScore'
import WellsSubHeader from './WellsSubHeader'
import PerformanceTableWellStatusDropdown from './PerformanceTableWellStatusDropdown'
import WellStatusOverlay from './common/WellStatusOverlay'
import CompareWellCheckbox from './common/CompareWellCheckbox'
import InferenceAvailabilityMessage from './common/InferenceAvailabilityMessage'
import WellPgtaDropdown from './WellPgtaDropdown'
import ClinicGardner from './ClinicGardner'

export default {
    name: 'PerformanceTable',
    components: {
        ClinicGardner,
        InferenceAvailabilityMessage,
        PerformanceTableWellStatusDropdown,
        WellStatusOverlay,
        CompareWellCheckbox,
        WellsSubHeader,
        BlastScore,
        KidScore,
        WellPgtaDropdown
    },
    computed: {
        ...mapState({
            selectedTreatment: state => state.timelapse.selectedTreatment,
            selectedForComparison: state => state.timelapse.selectedForComparison,
            currentView: state => state.navigation.currentView
        }),
        ...mapGetters({
            isFdaFriendly: 'common/isFdaFriendly',
            getPnCount: 'timelapse/getPnCount',
            getPnText: 'timelapse/getPnText',
            getPnHazard: 'timelapse/getPnHazard',
            checkDegeneratedOocyteHazard: 'timelapse/checkDegeneratedOocyteHazard',
            checkDucsHazard: 'timelapse/checkDucsHazard',
            checkFragmentation: 'timelapse/checkFragmentation',
            getHazards: 'timelapse/getHazards',
            isTooEarlyForScores: 'timelapse/isTooEarlyForScores',
            getSelectedInstrument: 'timelapse/getSelectedInstrument'
        }),
        slideIdLabel() {
            switch (this.getSelectedInstrument.type) {
                case INSTRUMENT_TYPE.GERI:
                    return this.$translate('inst_chamb')
                case INSTRUMENT_TYPE.MIRI:
                    return this.$translate('inc_comp')
                default:
                    return this.$translate('inst_slide')
            }
        },
        wells() {
            return this.selectedTreatment.wellsByIndex
        },
        commonColumns() {
            return [
                {
                    key: 'select',
                    label: this.$translate('select')
                },
                {
                    key: 'index',
                    label: this.$translate('e_id'),
                    sortable: true,
                    sortByFormatted: true,
                    stickyColumn: true,
                    formatter: (value, key, item) => this.wells.indexOf(item) + 1
                },
                {
                    key: 'esId',
                    label: this.$translate('well'),
                    sortable: true
                },
                {
                    key: 'slide.esId',
                    label: this.slideIdLabel,
                    sortable: false,
                    formatter: (value, key, item) => this.getInstSlide(item.slideId)
                },
                {
                    key: 'status',
                    sortable: false,
                    label: this.$translate('status'),
                    formatter: (value, key, item) => {
                        const { status = null } = this.selectedTreatment || {}
                        if (status === TREATMENT_STATUS.INACTIVE) {
                            const lastEvent = Utils.getLastMkEventWithImage(item)
                            if (lastEvent && lastEvent.imageUrl) return lastEvent.imageUrl
                        }
                        return item.lastImageUrl
                    }
                }
            ]
        },
        fdaUnfriendlyColumns() {
            return [
                {
                    key: 'kidRank',
                    sortable: true,
                    sortByFormatted: true,
                    formatter: value => {
                        return value != null ? value : Infinity
                    },
                    label: this.$translate('rank')
                },
                {
                    key: 'inferenceData.kidScore',
                    sortable: true,
                    sortByFormatted: true,
                    label: this.$translate('eq_score')
                },
                {
                    key: 'inferenceData.blastScore',
                    sortable: true,
                    sortByFormatted: true,
                    label: this.$translate('blast'),
                    formatter: (value, key, item) => {
                        // the values are (low to high) “N/A”(null) < “0.0-10.0” < blast(V)
                        const [MIN_BLAST_SCORE, MAX_BLAST_SCORE] = [0, 10]

                        if (item.isBlast) return MAX_BLAST_SCORE + 1

                        return value === null ? MIN_BLAST_SCORE - 1 : value
                    }
                },
                {
                    key: 'inferenceData.pnCount',
                    label: this.$translate('pn_count'),
                    sortable: false,
                    formatter: (value, key, item) => {
                        return this.getPnText(item)
                    }
                },
                {
                    key: 'annotationHazard',
                    label: this.$translate('abnor'),
                    sortable: false
                }
            ]
        },
        istanbulEventColumns() {
            const fields = []
            for (let i = 0; i < ALL_ISTANBUL_EVENTS.length; i++) {
                const istanbulEvent = ALL_ISTANBUL_EVENTS[i]
                fields.push({
                    key: `istanbulEvent_${i}`,
                    label: this.$translate(istanbulEvent.label),
                    sortable: true,
                    hoverText: istanbulEvent.tooltipText,
                    sortByFormatted: true,
                    formatter: (_, __, values) => {
                        const { mkEventsMap } = values
                        return Utils.mapIstanbulEvent(
                            mkEventsMap,
                            istanbulEvent,
                            this.selectedTreatment.hoursSinceFertilization
                        )
                    }
                })
            }
            return fields
        },
        mkEventColumns() {
            const fields = []
            for (let i = 0; i < ALL_MK_EVENTS.length; i++) {
                const mkEventSN = ALL_MK_EVENTS[i]

                fields.push({
                    key: `mkEventsMap.${mkEventSN}.value`,
                    label: this.$translate(mkEventSN),
                    variant: i % 2 === 0 ? 'light' : '',
                    sortable: true,
                    formatter: value => UIUtils.formatHourSinceFertilization(value)
                })
            }
            return fields
        },
        fields() {
            const fields = [...this.commonColumns]

            if (!this.isFdaFriendly) {
                fields.push(...this.fdaUnfriendlyColumns)
            }

            fields.push({
                key: 'pgta',
                label: this.$translate('pgta'),
                sortable: false
            })

            if (!this.isFdaFriendly) {
                fields.push(
                    ...(this.currentView === TREATMENT_PAGE_VIEWS.PERFORMANCE_TABLE
                        ? this.mkEventColumns
                        : this.istanbulEventColumns)
                )
            }

            fields.push({
                key: 'clinicGardner',
                label: this.$translate('c_gardner'),
                sortable: true,
                variant: 'light',
                formatter: value => UIUtils.formatGardnerScore(value)
            })

            return fields
        }
    },
    methods: {
        ducsHazard(well) {
            return this.checkDucsHazard(well) && well.excludeFromRanking
        },
        getRankValueText(well) {
            if (this.ducsHazard(well) || this.pnHazard(well) || this.degeneratedOocyteHazard(well)) return ''

            if (!well.kidRank) return this.$translate('na')

            return `#${well.kidRank}`
        },
        pnCount(well) {
            return this.getPnCount(well)
        },
        pnHazard(well) {
            return this.getPnHazard(well)
        },
        degeneratedOocyteHazard(well) {
            return this.checkDegeneratedOocyteHazard(well)
        },
        fragmented(well) {
            return this.checkFragmentation(well)
        },
        selectWell(well) {
            this.$mainController.selectWell(well)
        },
        rowClass(item, type) {
            if (!item || type !== 'row') return null

            let cssClass = 'performance-table-row'
            if (this.selectedForComparison.includes(item._id)) {
                cssClass += ' selected-for-compare'
            }

            return cssClass
        },
        getInstrumentEsId(slide) {
            const { instrumentId } = slide
            const instrument = this.selectedTreatment.instruments.find(
                instr => instr._id === instrumentId
            )

            return instrument.esId
        },
        getInstSlide(slideId) {
            const { slides, instruments } = this.selectedTreatment

            const slide = slides.find(_slide => _slide._id === slideId)

            const { instrumentId } = slide
            const instrument = instruments.find(instr => instr._id === instrumentId)

            if (
                slide.instrumentType &&
                [INSTRUMENT_TYPE.GERI, INSTRUMENT_TYPE.MIRI].includes(slide.instrumentType)
            )
                return `${instrument.esId} | #${slide.slidePosition}`

            return `I${instrument.esId} / ${slide.esId}`
        }
    }
}
</script>

<style lang="scss" scoped>
.performance-table {
    width: 100%;
    height: 100%;
    overflow: hidden;
    padding-bottom: 5rem;

    .performance-table-content {
        width: 100%;
        height: calc(100% - #{$wells-sub-header-height});
        display: flex;
        margin-top: 0.5rem;
        border-radius: $border-radius-lg;
        white-space: nowrap;

        .scrollable-part {
            width: 100%;
            height: 100%;
            max-height: 100%; // overrides
            @include hide-scrollbar();
        }

        .decision-cell {
            display: flex;
            justify-content: center;

            .latest-pic {
                position: relative;
                cursor: pointer;

                img {
                    height: 3.5rem;
                    width: 3.5rem;
                    border-radius: $border-radius;
                }
            }
        }
        .pgta-cell {
            width: 100%;
        }

        .rank-cell {
            position: relative;
            height: 3.5rem;
            width: 3.5rem;
            padding: 0.5rem;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: $border-radius;

            &.top-rank {
                background-color: $dark-2;

                .crown {
                    position: absolute;
                    overflow: hidden;
                    width: 1.5rem;
                    top: 0;
                    left: 0;
                }
            }
            &:not(.top-rank) {
                .crown {
                    display: none;
                }
            }

            .ai-icon {
                height: 0.9rem;
                margin-top: 0.25rem;
                margin-right: 0.25rem;
            }

            .rank-value {
                display: flex;
                align-items: center;
                white-space: nowrap;
                margin-bottom: -0.5rem;
            }

            .score-hazard-icons-container {
                margin-top: -0.3rem;

                img {
                    height: 1.1rem;
                    margin-left: 0.25rem;
                }
            }
        }

        .pn-count-cell {
            &.hazard {
                color: $red;
            }
        }

        .abnormalities-cell {
            display: flex;
            flex-direction: column;

            .duc {
                color: $duc;
            }
            .deg {
                color: $deg;
            }
            .frag {
                color: $frag;
            }
            .pn {
                color: $pn;
            }
        }
    }

    &.sticky-warning {
        .performance-table-content {
            // header + warning
            height: calc(100% - #{$wells-sub-header-height} * 2);
        }
    }
}
</style>

<style lang="scss">
.performance-table {
    .well-status {
        height: 3.5rem;
        min-width: 2rem;

        .back-colored {
            padding: 0.5rem;
            border-radius: 0.2rem;
        }
        .discard-button {
            border: 1px solid $red;
        }
        .pending-button {
            border: 1px solid $yellow;
        }
        .transferred-button {
            border: 1px solid $green;
        }
        .frozen-button {
            border: 1px solid $frozen-color;
        }
        .thawed-button {
            border: 1px solid $purple;
        }
        .select-button {
            border-radius: 0.2rem;
            border: 1px solid white;
            &svg {
                fill: #fff;
                stroke: #fff;
            }
        }

        &:hover {
            i {
                border-radius: 0.2rem;
                color: $light-3;
                font-size: 11pt !important;

                &.fa:before {
                    content: '\f078';
                }

                &.transferred-button,
                &.frozen-button,
                &.discard-button {
                    color: $light-3;
                    font-size: 12pt;
                    -webkit-text-stroke: 0 $light !important;
                }
            }
        }
    }

    .performance-table-content {
        th:not(.table-light) {
            background-color: $dark-2 !important;
        }

        th {
            background-color: darken($dark-2, 4) !important;
            border: none;
            color: $light-3 !important;
            font-weight: var(--font-weight-regular);
            font-size: 0.7vw;
            height: 3rem;

            &:hover {
                background-color: lighten($dark-3, 2) !important;

                &:not(.table-light) {
                    background-color: $dark-2 !important;
                }
            }

            td {
                text-align: center !important;
            }
        }

        .table-hover tr.selected-for-compare {
            background-color: $dark-5;

            td.table-light {
                background-color: darken($dark-5, 4) !important;
            }

            .discard-button {
                -webkit-text-stroke: 0.18rem $dark-5;
            }

            .transferred-button {
                -webkit-text-stroke: 0.15rem $dark-5;
            }
        }

        .table-hover tr {
            text-align: center;
            background-color: $dark-3;

            td.table-light {
                background-color: darken($dark-3, 4) !important;
            }

            .discard-button {
                -webkit-text-stroke: 0.18rem $dark-3;
            }

            .transferred-button {
                -webkit-text-stroke: 0.15rem $dark-3;
            }

            &:hover {
                background-color: $dark-4;

                td.table-light {
                    background-color: darken($dark-4, 4) !important;
                }

                .discard-button {
                    -webkit-text-stroke: 0.18rem $dark-4;
                }

                .transferred-button {
                    -webkit-text-stroke: 0.15rem $dark-4;
                }
            }
        }

        .performance-table-row {
            border-left: 0.1rem solid transparent;
            border-right: 0.1rem solid transparent;
            border-top: 0.1rem solid $dark;
            border-bottom: 0.1rem solid $dark;

            td {
                padding: 0.9vh 0.6vw;
                color: $light-3 !important;
                vertical-align: middle;
                font-size: 0.8vw;
                border-top: none !important;

                &.table-light {
                    background-color: $dark !important;
                }
            }
        }
    }

    @media screen and #{$new-break4close} {
        .performance-table-content {
            th {
                font-size: 10pt !important;
            }

            .performance-table-row {
                td {
                    padding: 0.6rem 0.3rem;
                    font-size: 10pt !important;
                }
            }
        }
    }
}
</style>
